import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronLeft, ChevronRight, Gift, Sparkles } from 'lucide-react';
import axios from 'axios';
import ProductCard from '@/components/ProductCard';
import { API } from '@/config';

export default function Home() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const carouselRef = useRef(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        await axios.post(`${API}/seed`);
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const categories = [
    { 
      name: 'Baby Items', 
      image: 'https://customer-assets.emergentagent.com/job_a5c2e806-76ac-45b9-a482-9007f3bbaad7/artifacts/ucw9690y_Photoroom_20260131_055956.png',
    },
    { 
      name: 'Bags', 
      displayName: 'Pouches & Bags',
      image: 'https://customer-assets.emergentagent.com/job_a5c2e806-76ac-45b9-a482-9007f3bbaad7/artifacts/8da5cv3m_Photoroom_20260214_115536.png',
    },
    { 
      name: 'Accessories', 
      image: 'https://customer-assets.emergentagent.com/job_a5c2e806-76ac-45b9-a482-9007f3bbaad7/artifacts/90pqyol8_PhotoRoom_20230704_212157.png',
    },
    { 
      name: 'Home Decor', 
      image: 'https://customer-assets.emergentagent.com/job_a5c2e806-76ac-45b9-a482-9007f3bbaad7/artifacts/8da5cv3m_Photoroom_20260214_115536.png',
    },
  ];

  // Most Popular products
  const popularNames = ['Tote Bag', 'Book Sleeve (Standard Size)', 'Hot/Cold Therapy Bag', 'Handmade Dribble Bib (Single)', 'Burp Cloth', 'Accessories Pouch'];
  const popularProducts = products.filter(product => 
    popularNames.includes(product.name)
  );

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 320;
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-[#FDE8F0]">
      {/* Announcement Bar */}
      <section className="bg-gradient-to-r from-[#9333EA] via-[#A855F7] to-[#EC4899] text-white py-3 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm md:text-base font-medium">
            Handmade in Australia • Small-batch creations • Custom orders welcome
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section className="py-10 md:py-16 px-4 md:px-8 bg-[#FDE8F0]">
        <div className="max-w-6xl mx-auto text-center">
          <img 
            src="https://customer-assets-wrfwihn1.emergentagent.net/job_94da6462-56e4-493d-84c9-8a6e648c341b/artifacts/d17flpwh_Photoroom_20260723_225436.png" 
            alt="Pockett Designs Logo"
            className="w-full max-w-[200px] md:max-w-[250px] mx-auto h-auto object-contain mb-6"
            data-testid="hero-banner"
          />
          
          {/* H1 - SEO optimized */}
          <h1 className="font-heading font-bold text-2xl md:text-4xl lg:text-5xl text-[#9333EA] mb-4 leading-tight">
            Handmade fabric gifts, baby essentials<br className="hidden md:block" /> and everyday accessories
          </h1>
          
          {/* Hero supporting text */}
          <p className="text-[#6B4E71] text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
            Thoughtfully sewn in small batches, with practical designs, playful prints and a personal touch. 
            Find something special or ask us to create it just for you.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Button 
              asChild
              className="bg-[#9333EA] hover:bg-[#7C3AED] rounded-full px-8 h-12 font-semibold text-white shadow-lg"
              data-testid="hero-shop-btn"
            >
              <Link to="/shop">
                Shop Handmade Gifts
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="rounded-full px-8 h-12 font-semibold border-2 border-[#9333EA] text-[#9333EA] hover:bg-[#9333EA] hover:text-white"
              data-testid="hero-custom-btn"
            >
              <a href="mailto:p.designs@mail.com">
                Request a Custom Order
              </a>
            </Button>
          </div>
          
          {/* Trust Strip */}
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-[#6B4E71]">
            <span className="flex items-center gap-1">
              <span className="text-[#9333EA]">✦</span> Handmade in Australia
            </span>
            <span className="flex items-center gap-1">
              <span className="text-[#9333EA]">✦</span> Small-batch & one-of-a-kind
            </span>
            <span className="flex items-center gap-1">
              <span className="text-[#9333EA]">✦</span> Free gift wrapping
            </span>
            <span className="flex items-center gap-1">
              <span className="text-[#9333EA]">✦</span> Custom orders welcome
            </span>
          </div>
        </div>
      </section>

      {/* Halloween Pre-Order Announcement */}
      <section className="py-8 md:py-12 px-4 md:px-8 bg-[#FDE8F0]">
        <div className="max-w-6xl mx-auto">
          {/* Spooky Frame */}
          <div className="relative bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1a] rounded-2xl p-6 md:p-10 border-4 border-[#FF6B00] shadow-2xl overflow-hidden">
            
            {/* Spooky Corner Decorations */}
            <div className="absolute top-3 left-3 text-2xl md:text-3xl">🎃</div>
            <div className="absolute top-3 right-3 text-2xl md:text-3xl">🦇</div>
            <div className="absolute bottom-3 left-3 text-2xl md:text-3xl">💀</div>
            <div className="absolute bottom-3 right-3 text-2xl md:text-3xl">🕷️</div>
            
            {/* Orange accent line at top */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-[#FF6B00] to-transparent"></div>
            
            {/* Spiderweb decoration */}
            <div className="absolute top-0 right-0 w-24 h-24 opacity-20">
              <svg viewBox="0 0 100 100" className="w-full h-full text-purple-400">
                <path d="M100 0 L50 50 M100 50 L50 50 M100 100 L50 50 M50 0 L50 50 M0 50 L50 50 M50 100 L50 50" stroke="currentColor" strokeWidth="1" fill="none"/>
                <circle cx="50" cy="50" r="10" stroke="currentColor" strokeWidth="1" fill="none"/>
                <circle cx="50" cy="50" r="25" stroke="currentColor" strokeWidth="1" fill="none"/>
                <circle cx="50" cy="50" r="40" stroke="currentColor" strokeWidth="1" fill="none"/>
              </svg>
            </div>
            
            <div className="text-center mb-6">
              <p className="text-[#FF6B00] text-sm font-semibold tracking-widest uppercase mb-2">
                Orders open 1 September
              </p>
              <h2 style={{fontFamily: "'Creepster', cursive"}} className="font-bold text-3xl md:text-5xl text-[#9333EA] mb-3 drop-shadow-[0_0_10px_rgba(147,51,234,0.5)]">
                Reusable Halloween candy bags, made your way
              </h2>
              <p className="text-gray-300 text-base md:text-lg mt-2 max-w-xl mx-auto">
                Choose your outer and lining fabrics to create a one-of-a-kind trick-or-treat bag. 
                Handmade for $17 each, with larger sizes available by request.
              </p>
            </div>
            
            {/* Halloween Product Image */}
            <div className="flex justify-center items-center gap-4 md:gap-8 mb-6">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_94da6462-56e4-493d-84c9-8a6e648c341b/artifacts/culnjpic_IMG_2173.jpeg"
                alt="Halloween Candy Bag - Bats & Moons"
                className="h-48 md:h-72 w-auto object-contain drop-shadow-[0_0_20px_rgba(147,51,234,0.4)] rounded-lg"
              />
            </div>
            
            {/* Fabric Swatches Preview */}
            <div className="mb-6">
              <p className="text-center text-gray-400 text-sm mb-3">Choose your outer & inner fabrics:</p>
              <div className="flex justify-center flex-wrap gap-2 md:gap-3">
                <div className="text-center">
                  <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_94da6462-56e4-493d-84c9-8a6e648c341b/artifacts/kctgfzo1_IMG_2230.jpeg" alt="Spooky Dogs" className="w-14 h-14 md:w-16 md:h-16 object-cover rounded-lg border-2 border-[#FF6B00] hover:scale-110 transition-transform" />
                  <p className="text-gray-400 text-xs mt-1">Spooky Dogs</p>
                </div>
                <div className="text-center">
                  <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_94da6462-56e4-493d-84c9-8a6e648c341b/artifacts/qc3mh26c_IMG_2231.jpeg" alt="Witchy Vibes" className="w-14 h-14 md:w-16 md:h-16 object-cover rounded-lg border-2 border-[#9333EA] hover:scale-110 transition-transform" />
                  <p className="text-gray-400 text-xs mt-1">Witchy Vibes</p>
                </div>
                <div className="text-center">
                  <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_94da6462-56e4-493d-84c9-8a6e648c341b/artifacts/qw6sum7j_IMG_2232.jpeg" alt="Black Cats" className="w-14 h-14 md:w-16 md:h-16 object-cover rounded-lg border-2 border-[#FF6B00] hover:scale-110 transition-transform" />
                  <p className="text-gray-400 text-xs mt-1">Black Cats</p>
                </div>
                <div className="text-center">
                  <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_94da6462-56e4-493d-84c9-8a6e648c341b/artifacts/qko1ecza_IMG_2233.jpeg" alt="Dancing Skeletons" className="w-14 h-14 md:w-16 md:h-16 object-cover rounded-lg border-2 border-[#FF6B00] hover:scale-110 transition-transform" />
                  <p className="text-gray-400 text-xs mt-1">Skeletons</p>
                </div>
                <div className="text-center">
                  <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_94da6462-56e4-493d-84c9-8a6e648c341b/artifacts/56b4fnq8_IMG_2235.jpeg" alt="Halloween Magic" className="w-14 h-14 md:w-16 md:h-16 object-cover rounded-lg border-2 border-[#9333EA] hover:scale-110 transition-transform" />
                  <p className="text-gray-400 text-xs mt-1">Magic</p>
                </div>
                <div className="text-center">
                  <img src="https://customer-assets-wrfwihn1.emergentagent.net/job_94da6462-56e4-493d-84c9-8a6e648c341b/artifacts/culnjpic_IMG_2173.jpeg" alt="Neon Bats" className="w-14 h-14 md:w-16 md:h-16 object-cover rounded-lg border-2 border-[#9333EA] hover:scale-110 transition-transform" />
                  <p className="text-gray-400 text-xs mt-1">Neon Bats</p>
                </div>
              </div>
              <p className="text-center text-[#FF6B00] text-xs mt-2">+ more fabrics coming!</p>
            </div>
            
            <div className="text-center">
              <p className="text-gray-400 text-sm md:text-base mb-2">
                Handmade candy bags • Pick your outer & inner fabric
              </p>
              <p className="text-[#FF6B00] text-sm md:text-base mb-4">
                Need a larger size? Send a custom order enquiry!
              </p>
              <a 
                href="https://instagram.com/pockett.designs" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#9333EA] hover:bg-[#7928CA] text-white font-semibold px-6 py-3 rounded-full transition-colors shadow-lg shadow-purple-500/30"
              >
                Follow for Updates
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
            
            {/* Purple accent line at bottom */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-[#9333EA] to-transparent"></div>
          </div>
        </div>
      </section>

      {/* Most Popular Section */}
      <section className="py-8 md:py-12 px-4 md:px-8 bg-[#FDE8F0]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#9333EA]">
              Handmade Favourites
            </h2>
            
            {/* Carousel Navigation */}
            <div className="hidden md:flex gap-2">
              <Button 
                variant="outline" 
                size="icon" 
                className="rounded-full border-2 border-[#9333EA] text-[#9333EA] hover:bg-[#9333EA] hover:text-white h-10 w-10"
                onClick={() => scrollCarousel('left')}
                data-testid="carousel-left"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                className="rounded-full border-2 border-[#9333EA] text-[#9333EA] hover:bg-[#9333EA] hover:text-white h-10 w-10"
                onClick={() => scrollCarousel('right')}
                data-testid="carousel-right"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex gap-4 overflow-x-auto pb-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex-shrink-0 w-64 bg-white rounded-xl overflow-hidden animate-pulse">
                  <div className="aspect-square bg-gray-200" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                    <div className="h-4 bg-gray-200 rounded w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div 
              ref={carouselRef}
              className="flex gap-4 overflow-x-auto pb-4 scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {popularProducts.map((product, index) => (
                <div 
                  key={product.id}
                  className="flex-shrink-0 w-64"
                >
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          )}
          
          <div className="mt-6 text-center">
            <Button 
              variant="outline"
              asChild
              className="rounded-full border-2 border-[#9333EA] text-[#9333EA] hover:bg-[#9333EA] hover:text-white"
            >
              <Link to="/shop">
                View All Products
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-8 md:py-12 px-4 md:px-8 bg-[#FDE8F0]">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#9333EA] mb-6 text-center">
            Shop by Category
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.map((category, index) => (
              <Link 
                key={`${category.name}-${index}`}
                to={`/shop?category=${encodeURIComponent(category.name)}`}
                className="group relative overflow-hidden rounded-xl bg-white shadow-sm hover:shadow-lg transition-all"
                data-testid={`category-${(category.displayName || category.name).toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={category.image}
                    alt={category.displayName || category.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-4 text-center bg-white">
                  <h3 className="font-semibold text-[#9333EA]">
                    {category.displayName || category.name}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Shop With Us - SEO Section */}
      <section className="py-8 md:py-12 px-4 md:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#9333EA] mb-8 text-center">
            Why Choose Pockett Designs?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#FDE8F0] rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#9333EA]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-lg text-[#9333EA] mb-2">Handmade With Love</h3>
              <p className="text-gray-600 text-sm">
                Every item is individually crafted by hand in Australia. No mass production — just quality, care, and attention to detail in every stitch.
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#FDE8F0] rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#9333EA]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
              </div>
              <h3 className="font-semibold text-lg text-[#9333EA] mb-2">Unique Fabric Choices</h3>
              <p className="text-gray-600 text-sm">
                Choose from a wide variety of fun, colourful fabric prints. From florals to cute characters — find the perfect pattern to match your style.
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#FDE8F0] rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-[#9333EA]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              </div>
              <h3 className="font-semibold text-lg text-[#9333EA] mb-2">Perfect For Gifting</h3>
              <p className="text-gray-600 text-sm">
                Looking for a thoughtful, unique gift? Each order can include free gift wrapping and a handwritten card. Baby showers, birthdays, or just because!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Orders Section */}
      <section className="py-8 md:py-12 px-4 md:px-8 bg-[#FDE8F0]">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 text-center shadow-sm">
            <Sparkles className="h-10 w-10 text-[#9333EA] mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#9333EA] mb-4">
              Have something special in mind?
            </h2>
            <p className="text-gray-600 mb-6 max-w-lg mx-auto">
              Looking for a particular print, size or design? Tell us what you have in mind and 
              we'll help create a handmade piece that feels completely yours.
            </p>
            <Button 
              asChild
              className="bg-[#9333EA] hover:bg-[#7C3AED] rounded-full px-8 h-12 font-semibold text-white"
              data-testid="custom-order-btn"
            >
              <a href="mailto:p.designs@mail.com">
                Start a Custom Order
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Free Gift Wrap Section */}
      <section className="py-8 md:py-12 px-4 md:px-8 bg-[#FDE8F0]">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 text-center shadow-sm">
            <Gift className="h-10 w-10 text-[#9333EA] mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#9333EA] mb-4">
              Free gift wrapping and handwritten card
            </h2>
            <p className="text-gray-600 mb-6 max-w-lg mx-auto">
              Sending your order straight to someone special? Add a message at checkout 
              and we'll wrap it beautifully with a handwritten card at no extra cost.
            </p>
            <Button 
              asChild
              variant="outline"
              className="rounded-full px-8 h-12 font-semibold border-2 border-[#9333EA] text-[#9333EA] hover:bg-[#9333EA] hover:text-white"
              data-testid="shop-gifts-btn"
            >
              <Link to="/shop">
                Shop Gifts
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
